import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { CheckCircle2, CalendarDays, MapPin } from 'lucide-react';
import useFetch from '../../hooks/useFetch';
import { post } from '../../api/client';
import { useToast } from '../../context/ToastContext';
import CheckInForm from '../../components/CheckInForm';
import Logo from '../../components/Logo';
import { Card, Loader, EmptyState, Button } from '../../components/UI';
import { fmtDate } from '../../utils/format';

/**
 * Checking in from a link or a QR code, with nobody signed in.
 *
 * The code on the welcome desk opens this page in whatever camera app read it.
 * People are recognised by the one detail the organiser chose to ask for —
 * their employee id, mobile or email — matched against the event's own roster,
 * so only people who are expected can check themselves in.
 */
export default function CheckInLink() {
  const { slug } = useParams();
  const toast = useToast();
  const { data, loading, error } = useFetch(`/attendance/checkin/${slug}`);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);
  // Set when the detail given is not on the event's list and the organiser
  // lets people add themselves at the gate.
  const [addSelf, setAddSelf] = useState(false);
  const [company, setCompany] = useState('');

  const submit = async (responses) => {
    setBusy(true);
    try {
      const res = await post(`/attendance/checkin/${slug}`, {
        responses,
        ...(addSelf ? { walkIn: true, ...(company ? { corporate: company } : {}) } : {}),
      });
      if (res.data?.notOnList) {
        // Not on the list, but they may add themselves — ask for the rest.
        setAddSelf(true);
        toast.info(res.message);
        return;
      }
      setDone({ ...res.data, message: res.message });
    } catch (err) {
      toast.error(err.message);
    } finally {
      setBusy(false);
    }
  };

  if (loading) return <Loader full label="Opening check-in" />;

  if (error || !data) {
    return (
      <Shell>
        <EmptyState
          message={error || 'This check-in link is not valid'}
          hint="Ask at the welcome desk — they can check you in there."
        />
      </Shell>
    );
  }

  const { event, form } = data;

  return (
    <Shell>
      <Card padding={20} style={{ marginBottom: 16 }}>
        <p style={{ fontSize: 11.5, color: 'var(--text-lo)', textTransform: 'uppercase', letterSpacing: 0.9, fontWeight: 600 }}>
          Check in
        </p>
        <h1 style={{ fontSize: 22, fontWeight: 800, marginTop: 6 }}>{event.title}</h1>
        <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', marginTop: 8, fontSize: 12.5, color: 'var(--text-mid)' }}>
          <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <CalendarDays size={13} color="var(--gold-500)" /> {fmtDate(event.startDate)}
          </span>
          {event.venue?.name && (
            <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <MapPin size={13} color="var(--gold-500)" /> {event.venue.name}{event.venue.city ? `, ${event.venue.city}` : ''}
            </span>
          )}
        </div>
      </Card>

      {done ? (
        <motion.div initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }}>
          <Card padding={28} style={{ textAlign: 'center' }}>
            <div style={{ width: 64, height: 64, borderRadius: 99, background: 'rgba(74,222,128,0.14)', display: 'grid', placeItems: 'center', margin: '0 auto 14px' }}>
              <CheckCircle2 size={32} color="var(--ok)" />
            </div>
            <h2 style={{ fontSize: 26, fontWeight: 800, color: 'var(--ok)' }}>
              {done.already ? 'Already checked in' : 'You’re in!'}
            </h2>
            <p style={{ fontSize: 16, fontWeight: 600, marginTop: 8 }}>{done.name}</p>
            <p style={{ fontSize: 13.5, color: 'var(--text-mid)', marginTop: 6, lineHeight: 1.6 }}>
              {done.companions
                ? `${done.people} people checked in — you and ${done.companions} ${done.companions === 1 ? 'other' : 'others'}.`
                : 'Checked in on your own.'}
            </p>
            {done.addedToList && (
              <p style={{ fontSize: 12.5, color: 'var(--gold-300)', marginTop: 10 }}>
                You were not on the list, so we have added you.
              </p>
            )}
            <p style={{ fontSize: 13, color: 'var(--text-lo)', marginTop: 14 }}>
              Enjoy {event.title}. Show this screen at the desk if anyone asks.
            </p>
            <Button variant="outline" size="sm" onClick={() => { setDone(null); setAddSelf(false); }} style={{ marginTop: 18 }}>
              Check someone else in
            </Button>
          </Card>
        </motion.div>
      ) : (
        <CheckInForm
          form={form}
          busy={busy}
          onSubmit={submit}
          addSelf={addSelf}
          companies={data.companies || []}
          company={company}
          onCompany={setCompany}
          title={addSelf ? 'Add yourself to the list' : undefined}
          note={addSelf
            ? `We could not find that ${form.identifierLabel} on the list. Fill this in and you will be added and checked in.`
            : undefined}
        />
      )}

      <p style={{ fontSize: 11.5, color: 'var(--text-lo)', textAlign: 'center', marginTop: 18, lineHeight: 1.7 }}>
        {form.allowWalkIns
          ? 'If you are not on the list you can add yourself here — the organisers will see you came in at the gate.'
          : 'Only people already on this event’s list can check in here. If your details are not recognised, the welcome desk can do it for you.'}
      </p>
    </Shell>
  );
}

function Shell({ children }) {
  return (
    <div style={{ minHeight: '100vh', background: 'var(--ink-950)', padding: '28px 16px 48px' }}>
      <div style={{ maxWidth: 520, margin: '0 auto' }}>
        <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 22 }}>
          <Logo size={34} />
        </div>
        {children}
      </div>
    </div>
  );
}
